import React, { useState } from 'react';
import useApi from '../../hooks/useApi';
import { ManagedUser } from '../../types';
import styles from './EditUserModal.module.css';

interface EditUserModalProps {
  user: ManagedUser;
  onClose: () => void;
  onUserUpdated: (updatedUser: ManagedUser) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onUserUpdated }) => {
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.user_metadata?.firstName || '');
  const [lastName, setLastName] = useState(user.user_metadata?.lastName || '');
  const [partnerGroup, setPartnerGroup] = useState(user.user_metadata?.partner || '');
  const [formError, setFormError] = useState<string | null>(null);
  const { securedRequest, loading } = useApi();

  const hasChanges =
    email.trim() !== user.email ||
    firstName.trim() !== (user.user_metadata?.firstName || '') ||
    lastName.trim() !== (user.user_metadata?.lastName || '') ||
    partnerGroup.trim() !== (user.user_metadata?.partner || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const body = {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      partnerGroup: partnerGroup.trim(),
    };

    const { data, error } = await securedRequest(
      'patch',
      `/api/admin/users/${encodeURIComponent(user.userId)}`,
      body
    );

    if (error) {
      const message = typeof error === 'object' && error.error ? error.error : 'Failed to update user.';
      setFormError(message);
      return;
    }

    onUserUpdated(data);
    onClose();
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} card`}>
        <div className="card-body">
          <h5 className="card-title">Edit User</h5>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="editUserFirstName" className="form-label">First Name</label>
              <input
                id="editUserFirstName"
                type="text"
                className="form-control"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editUserLastName" className="form-label">Last Name</label>
              <input
                id="editUserLastName"
                type="text"
                className="form-control"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editUserPartnerGroup" className="form-label">Partner Group</label>
              <input
                id="editUserPartnerGroup"
                type="text"
                className="form-control"
                value={partnerGroup}
                onChange={e => setPartnerGroup(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editUserEmail" className="form-label">Email address</label>
              <input
                id="editUserEmail"
                type="email"
                className="form-control"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              {email.trim() !== user.email && (
                <small className="text-warning">
                  Changing the email will require the user to re-verify.
                </small>
              )}
            </div>
            {formError && <div className="alert alert-danger py-2">{formError}</div>}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || !hasChanges}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
